/**
 * RevenueCat SDK Initialization
 * 
 * This module initializes the RevenueCat SDK as early as possible in the app lifecycle.
 * It must run BEFORE any other code tries to use the SDK (e.g., AuthContext).
 */

import { Capacitor } from '@capacitor/core';

// RevenueCat API Key (public/publishable)
const REVENUECAT_API_KEY = import.meta.env.VITE_REVENUECAT_API_KEY || '';

// Track initialization state
let isConfigured = false;
let configurationPromise: Promise<boolean> | null = null;

/**
 * Initialize RevenueCat SDK. Safe to call multiple times - will only configure once.
 * Returns true if SDK is ready to use, false otherwise.
 */
export async function initializeRevenueCat(): Promise<boolean> {
  // Not on native platform - nothing to do
  if (!Capacitor.isNativePlatform()) {
    console.log('RevenueCat: Skipping init - not on native platform');
    return false;
  }

  // Already configured
  if (isConfigured) {
    return true;
  }

  // Configuration already in progress - wait for it
  if (configurationPromise) {
    return configurationPromise;
  }

  // Start configuration
  configurationPromise = doConfiguration();
  return configurationPromise;
}

async function doConfiguration(): Promise<boolean> {
  const platform = Capacitor.getPlatform();

  console.log('RevenueCat: Starting SDK configuration', {
    platform,
    hasApiKey: Boolean(REVENUECAT_API_KEY),
    apiKeyPrefix: REVENUECAT_API_KEY ? REVENUECAT_API_KEY.substring(0, 12) + '...' : 'missing',
  });

  if (!REVENUECAT_API_KEY) {
    console.error('RevenueCat: API key not configured - check VITE_REVENUECAT_API_KEY in .env');
    return false;
  }

  try {
    const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');

    // Set log level for debugging
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

    // Configure the SDK
    console.log('RevenueCat: Calling Purchases.configure() with key:', REVENUECAT_API_KEY.substring(0, 12) + '...');
    await Purchases.configure({ apiKey: REVENUECAT_API_KEY });

    isConfigured = true;
    console.log('RevenueCat: SDK configured successfully on', platform);
    return true;
  } catch (err) {
    console.error('RevenueCat: SDK configuration failed', {
      error: err,
      message: err instanceof Error ? err.message : 'Unknown error',
    });
    return false;
  }
}

/**
 * Check if RevenueCat SDK is configured and ready to use.
 */
export function isRevenueCatConfigured(): boolean {
  return isConfigured;
}
